import { lazy, Suspense, useEffect, useState } from "react";
import Seo from "@/components/seo/Seo";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";

const AboutSection = lazy(() => import("@/components/AboutSection"));
const CompositionSection = lazy(() => import("@/components/CompositionSection"));
const BenefitsSection = lazy(() => import("@/components/BenefitsSection"));
const HealthConditionsSection = lazy(() => import("@/components/HealthConditionsSection"));
const ScienceSection = lazy(() => import("@/components/ScienceSection"));
const FullereneSection = lazy(() => import("@/components/FullereneSection"));
const TechnologySection = lazy(() => import("@/components/TechnologySection"));
const UsageSection = lazy(() => import("@/components/UsageSection"));
const ProductLineSection = lazy(() => import("@/components/ProductLineSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const FaqSection = lazy(() => import("@/components/FaqSection"));
const OrderSection = lazy(() => import("@/components/OrderSection"));
const Footer = lazy(() => import("@/components/Footer"));
const FloatingBottle = lazy(() => import("@/components/FloatingBottle"));
const FloatingNav = lazy(() => import("@/components/FloatingNav"));

const Index = () => {
  const [deferredReady, setDeferredReady] = useState(false);

  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const trigger = () => setDeferredReady(true);

    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(trigger, { timeout: 2000 });
    } else {
      timeoutId = window.setTimeout(trigger, 1200);
    }

    // Also trigger early on first user interaction so nothing is missing if they scroll fast
    const onInteract = () => trigger();
    window.addEventListener("scroll", onInteract, { once: true, passive: true });
    window.addEventListener("pointerdown", onInteract, { once: true });

    return () => {
      if (idleId !== undefined && (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback) {
        (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback!(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("pointerdown", onInteract);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Seo canonicalPath="/" />
      <Header />
      <HeroSection />
      {deferredReady && (
        <Suspense fallback={null}>
          <FloatingBottle />
          <FloatingNav />
          <AboutSection />
          <CompositionSection />
          <BenefitsSection />
          <HealthConditionsSection />
          <ScienceSection />
          <FullereneSection />
          <TechnologySection />
          <UsageSection />
          <ProductLineSection />
          <TestimonialsSection />
          <FaqSection />
          <OrderSection />
          <Footer />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
